import { useEffect, useState } from "react";
import api from "@/lib/api";
import { inr, compact } from "@/lib/format";
import { Skeleton } from "@/components/Shared";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";

export const Pnl = () => {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/tools/pnl").then(({ data }) => setData(data));
  }, []);

  if (!data) return <Skeleton className="h-80" />;
  const chart = [...data.months].reverse();

  return (
    <div className="space-y-6" data-testid="pnl-tool">
      <p className="text-sm text-muted-foreground">Month-wise P&L economics for Amazon.in. {data.note}</p>
      {data.months.length === 0 ? (
        <p className="p-6 text-sm text-muted-foreground bg-card border border-border rounded-lg">No monthly data yet — appears as the order backfill progresses.</p>
      ) : (
        <>
          <div className="bg-card border border-border rounded-lg p-6 shadow-sm" data-testid="pnl-chart">
            <h3 className="font-heading text-base font-semibold tracking-tight mb-5">Revenue vs Fees vs Net by month</h3>
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={chart}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="hsl(var(--border))" />
                <XAxis dataKey="month" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => compact(v)} stroke="hsl(var(--muted-foreground))" width={80} />
                <Tooltip formatter={(v, n) => [inr(v), n]} contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="revenue" name="Revenue" fill="#2563EB" radius={[3, 3, 0, 0]} />
                <Bar dataKey="fees" name="Fees" fill="#D97706" radius={[3, 3, 0, 0]} />
                <Bar dataKey="net" name="Net" fill="#16A34A" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="bg-card border border-border rounded-lg shadow-sm overflow-x-auto" data-testid="pnl-table">
            <table className="w-full text-sm">
              <thead className="bg-muted/60 text-xs uppercase tracking-wider text-muted-foreground">
                <tr>{["Month", "Revenue", "Orders", "Units", "AOV", "Fees", "Fee %", "Refunds", "Ad Spend", "Net after Ads", "Payouts Received"].map((h) => <th key={h} className="text-left px-4 py-3 font-semibold">{h}</th>)}</tr>
              </thead>
              <tbody>
                {data.months.map((m) => (
                  <tr key={m.month} className="border-t border-border hover:bg-muted/40 transition-colors" data-testid={`pnl-row-${m.month}`}>
                    <td className="px-4 py-3 font-semibold">{m.month}</td>
                    <td className="px-4 py-3">{inr(m.revenue)}</td>
                    <td className="px-4 py-3">{m.orders.toLocaleString("en-IN")}</td>
                    <td className="px-4 py-3">{m.units.toLocaleString("en-IN")}</td>
                    <td className="px-4 py-3">{inr(m.aov)}</td>
                    <td className="px-4 py-3 text-red-600">{inr(m.fees)}</td>
                    <td className="px-4 py-3">{m.fee_pct}%</td>
                    <td className="px-4 py-3 text-red-600">{inr(m.refunds)}</td>
                    <td className="px-4 py-3 text-red-600">{m.ad_spend ? inr(m.ad_spend) : "—"}</td>
                    <td className="px-4 py-3 font-semibold text-green-600">{inr(m.net_after_ads ?? m.net)}</td>
                    <td className="px-4 py-3">{inr(m.payout)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};
